import { useState, useEffect, useContext, useCallback } from "react";
import {
    Table,
    Typography,
    Spin,
    Tag,
    Input,
    Button,
    Popconfirm,
    Space,
    Row,
    Col,
    Select,
    DatePicker,
} from "antd";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { NavigationContext } from "../../context/NavigationContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";

const { Title } = Typography;
const { RangePicker } = DatePicker;

export default function FinalSettlement() {
    const { authData, backUrl } = useContext(AuthContext);

    const [settlements, setSettlements] = useState([]);
    const [filteredData, setFilteredData] = useState([]);

    const [loading, setLoading] = useState(true);
    const [editingKey, setEditingKey] = useState(null);
    const [editedRecord, setEditedRecord] = useState({});

    // 🔹 FILTER STATES
    const [searchName, setSearchName] = useState("");
    const [searchCode, setSearchCode] = useState("");

    const [organisation, setOrganisation] = useState(null);
    const [department, setDepartment] = useState(null);
    const [designation, setDesignation] = useState(null);
    const [status, setStatus] = useState("");
    const [dateRange, setDateRange] = useState([]);

    // Dropdown data
    const [organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    // 🔹 Fetch settlements
    const fetchSettlements = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${backUrl}/api/payroll/settlements/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });

            const data = res.data.results || res.data;
            setSettlements(data);
            setFilteredData(data);
        } catch (err) {
            toast.error("Failed to load final settlements");
        } finally {
            setLoading(false);
        }
    }, [backUrl, authData]);

    // 🔹 Organisation dropdown data
    const fetchOrganizations = useCallback(async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`);
            setOrganisations(res.data.results);
        } catch (err) {
            console.error("Failed to fetch organizations", err);
        }
    }, [backUrl]);

    const fetchDepartments = async (orgId) => {
        if (!orgId) return setDepartments([]);
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/departments/?organisation=${orgId}`
            );
            setDepartments(res.data.results);
        } catch (err) {
            console.error("Failed to fetch departments", err);
        }
    };

    const fetchDesignations = async (depId) => {
        if (!depId) return setDesignations([]);
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/designations/?department=${depId}`
            );
            setDesignations(res.data.results);
        } catch (err) {
            console.error("Failed to fetch designations", err);
        }
    };

    const { navigationParams, clearNavigationParams } = useContext(NavigationContext);

    useEffect(() => {
        if (navigationParams?.status) {
            console.log("🎯 FinalSettlement: Syncing status from navigation:", navigationParams.status);
            // FinalSettlement uses ALL CAPS: "PENDING", "APPROVED", "REJECTED"
            const upperStatus = navigationParams.status.toUpperCase();
            if (["PENDING", "APPROVED", "REJECTED"].includes(upperStatus)) {
                setStatus(upperStatus);
            }
            clearNavigationParams();
        }
    }, [navigationParams, clearNavigationParams]);

    useEffect(() => {
        fetchSettlements();
        fetchOrganizations();
    }, [fetchSettlements, fetchOrganizations]);

    // 🔹 Auto Filter Logic
    useEffect(() => {
        let data = [...settlements];

        if (searchName)
            data = data.filter((i) =>
                i.employee_name?.toLowerCase().includes(searchName.toLowerCase())
            );

        if (searchCode)
            data = data.filter((i) =>
                i.emp_code?.toLowerCase().includes(searchCode.toLowerCase())
            );

        if (organisation)
            data = data.filter((i) => i.organisation_id === organisation);

        if (department)
            data = data.filter((i) => i.department_id === department);

        if (designation)
            data = data.filter((i) => i.designation_id === designation);

        if (status)
            data = data.filter((i) => i.status === status);

        if (dateRange.length === 2) {
            const [start, end] = dateRange;
            data = data.filter((i) => {
                if (!i.date_of_leaving) return false;
                const d = dayjs(i.date_of_leaving);
                return d.isAfter(start) && d.isBefore(end);
            });
        }

        setFilteredData(data);
    }, [
        searchName,
        searchCode,
        organisation,
        department,
        designation,
        status,
        dateRange,
        settlements,
    ]);

    // 🔹 Inline Editing
    const isEditing = (record) => record.id === editingKey;

    const edit = (record) => {
        setEditingKey(record.id);
        setEditedRecord({ ...record });
    };

    const cancel = () => {
        setEditingKey(null);
        setEditedRecord({});
    };

    const save = async (id) => {
        try {
            await axios.post(
                `${backUrl}/api/payroll/settlements/${id}/submit/`,
                editedRecord,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );

            toast.success("Settlement updated!");
            setEditingKey(null);
            fetchSettlements();
        } catch (err) {
            toast.error("Update failed");
        }
    };

    const approveSettlement = async (id) => {
        try {
            await axios.post(
                `${backUrl}/api/payroll/settlements/${id}/approve/`,
                {},
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );

            toast.success("Approved!");
            fetchSettlements();
        } catch (err) {
            toast.error("Approve failed");
        }
    };

    const handleInputChange = (key, value) => {
        setEditedRecord({ ...editedRecord, [key]: value });
    };

    // 🔹 Columns
    const columns = [
        { title: "Employee Name", dataIndex: "employee_name", editable: true },
        { title: "Employee Email", dataIndex: "employee_email", editable: true },
        { title: "Emp Code", dataIndex: "emp_code", editable: true },
        { title: "Department", dataIndex: "department", editable: true },
        { title: "Designation", dataIndex: "designation", editable: true },
        { title: "Location", dataIndex: "location", editable: true },

        { title: "Date of Leaving", dataIndex: "date_of_leaving" },

        {
            title: "Status",
            dataIndex: "status",
            render: (val) => (
                <Tag
                    color={
                        val === "APPROVED"
                            ? "green"
                            : val === "PENDING"
                                ? "orange"
                                : "red"
                    }
                >
                    {val}
                </Tag>
            ),
        },

        {
            title: "Actions",
            key: "actions",
            render: (_, record) => {
                const editable = isEditing(record);
                return (
                    <Space>
                        {editable ? (
                            <>
                                <Button
                                    type="primary"
                                    size="small"
                                    onClick={() => save(record.id)}
                                >
                                    Save
                                </Button>
                                <Popconfirm title="Cancel changes?" onConfirm={cancel}>
                                    <Button size="small">Cancel</Button>
                                </Popconfirm>
                            </>
                        ) : (
                            <Button
                                type="link"
                                onClick={() => edit(record)}
                                disabled={editingKey !== null}
                            >
                                Edit
                            </Button>
                        )}

                        {authData?.employee?.is_superuser &&
                            record.status !== "APPROVED" && (
                                <Popconfirm
                                    title="Approve this settlement?"
                                    onConfirm={() => approveSettlement(record.id)}
                                >
                                    <Button type="primary" size="small">
                                        Approve
                                    </Button>
                                </Popconfirm>
                            )}
                    </Space>
                );
            },
        },
    ];

    const mergedColumns = columns.map((col) => {
        if (!col.editable) return col;
        return {
            ...col,
            render: (text, record) =>
                isEditing(record) ? (
                    <Input
                        value={editedRecord[col.dataIndex]}
                        onChange={(e) =>
                            handleInputChange(col.dataIndex, e.target.value)
                        }
                    />
                ) : (
                    text
                ),
        };
    });

    return (
        <div style={{ maxWidth: "95%", margin: "40px auto" }}>
            <Title level={4}>Final Settlement Records</Title>

            {/* ▓▓▓ FILTER BOX ▓▓▓ */}
            <div
                style={{
                    background: "#ffffffff",
                    padding: 15,
                    marginBottom: 20,
                    borderRadius: 8,
                }}
            >
                <Row gutter={12}>
                    <Col span={6}>
                        <Input
                            placeholder="Search Employee Name"
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                        />
                    </Col>

                    <Col span={4}>
                        <Input
                            placeholder="Search Emp Code"
                            value={searchCode}
                            onChange={(e) => setSearchCode(e.target.value)}
                        />
                    </Col>

                    {/* Organisation */}
                    <Col span={4}>
                        <Select
                            style={{ width: "100%" }}
                            placeholder="Organisation"
                            allowClear
                            value={organisation}
                            onChange={(val) => {
                                setOrganisation(val);
                                setDepartment(null);
                                setDesignation(null);
                                fetchDepartments(val);
                            }}
                            options={organisations.map((d) => ({
                                label: d.name,
                                value: d.id,
                            }))}
                        />
                    </Col>

                    {/* Department */}
                    <Col span={4}>
                        <Select
                            style={{ width: "100%" }}
                            placeholder="Department"
                            allowClear
                            value={department}
                            onChange={(val) => {
                                setDepartment(val);
                                setDesignation(null);
                                fetchDesignations(val);
                            }}
                            options={departments.map((d) => ({
                                label: d.name,
                                value: d.id,
                            }))}
                        />
                    </Col>

                    {/* Designation */}
                    <Col span={4}>
                        <Select
                            style={{ width: "100%" }}
                            placeholder="Designation"
                            allowClear
                            value={designation}
                            onChange={setDesignation}
                            options={designations.map((d) => ({
                                label: d.name,
                                value: d.id,
                            }))}
                        />
                    </Col>

                    {/* Status */}
                    <Col span={4} style={{ marginTop: 10 }}>
                        <Select
                            style={{ width: "100%" }}
                            placeholder="Status"
                            allowClear
                            value={status}
                            onChange={setStatus}
                            options={[
                                { label: "PENDING", value: "PENDING" },
                                { label: "APPROVED", value: "APPROVED" },
                                { label: "REJECTED", value: "REJECTED" },
                            ]}
                        />
                    </Col>

                    {/* Date Range */}
                    <Col span={6} style={{ marginTop: 10 }}>
                        <RangePicker
                            style={{ width: "100%" }}
                            onChange={(v) => setDateRange(v || [])}
                        />
                    </Col>
                </Row>
            </div>

            {loading ? (
                <div style={{ textAlign: "center", padding: 50 }}>
                    <Spin size="large" />
                </div>
            ) : (
                <Table
                    columns={mergedColumns}
                    dataSource={filteredData}
                    rowKey="id"
                    bordered
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: "max-content" }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th {...props} style={{ backgroundColor: "#0e497b", color: 'white', marginTop: '20px' }}>
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            )}

            <ToastContainer position="top-center" autoClose={2500} />
        </div>
    );
}
